import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  FaCheckCircle,
  FaClock,
  FaEye,
  FaFilter,
  FaPrint,
  FaShoppingCart,
  FaSpinner,
  FaTruck,
} from 'react-icons/fa';
import ReusableTable from '../Reusable/EcomTable';
import ReusableModal from '../../AppPAges/ManageSettings/ReusableModal';
import { CustomButton } from '../../../common/ReusableComponent/CustomButton';
import useApi from '../../../../utils/api-manager/Helper/useApi';
import { useToastHandler } from '../../../common/ToastHandler';
import { useStore } from '../../../../context/StoreContext';
import './OrderManagement.css';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'ready_for_delivery', label: 'Ready For Delivery' },
  { value: 'out_for_delivery', label: 'Out For Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'cancelled_stock_returned', label: 'Canceled And Product Back Stock' },
  { value: 'refund_requested', label: 'Refund Requested' },
  { value: 'refunded', label: 'Refunded' },
];

const DATE_PRESETS = [
  { value: '', label: 'All Orders' },
  { value: 'today', label: "Today's Orders" },
  { value: 'yesterday', label: "Yesterday's Orders" },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const formatDateInput = (date) => date.toISOString().slice(0, 10);

const getPresetRange = (preset) => {
  const today = new Date();
  const start = new Date(today);
  const end = new Date(today);

  if (preset === 'today') return { from_date: formatDateInput(today), to_date: formatDateInput(today) };
  if (preset === 'yesterday') {
    start.setDate(today.getDate() - 1);
    return { from_date: formatDateInput(start), to_date: formatDateInput(start) };
  }
  if (preset === 'last_7_days') {
    start.setDate(today.getDate() - 6);
    return { from_date: formatDateInput(start), to_date: formatDateInput(end) };
  }
  if (preset === 'this_month') {
    start.setDate(1);
    return { from_date: formatDateInput(start), to_date: formatDateInput(end) };
  }
  return { from_date: '', to_date: '' };
};

const money = (value) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Number(value || 0));

const getOrderTotal = (order) => {
  if (order?.total_amount) return Number(order.total_amount);
  return order?.items?.reduce((sum, item) => sum + (Number(item.selling_price || 0) * Number(item.quantity || 0)), 0) || 0;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const getReadableAddress = (order) => {
  if (!order) return '-';

  if (order.full_address) return order.full_address;

  const addressValue = String(order.address || '').trim();
  const addressParts = [];

  if (addressValue && !UUID_PATTERN.test(addressValue)) {
    addressParts.push(addressValue);
  }

  addressParts.push(
    order.address_line1,
    order.address_line2,
    order.city,
    order.state,
    order.country,
    order.zipcode,
    order.postal_code
  );

  return addressParts.filter(Boolean).join(', ') || '-';
};

const normalizeStatus = (status, order) => (
  String(status || (order?.payment_method?.toLowerCase() === 'cod' ? 'pending' : 'completed')).toLowerCase()
);

const statusLabel = (status) => STATUS_OPTIONS.find(item => item.value === status)?.label || status || 'Pending';

const OrderManagement = () => {
  const [filters, setFilters] = useState({ status: '', preset: '', from_date: '', to_date: '' });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsModal, setDetailsModal] = useState(false);
  const [statusDraft, setStatusDraft] = useState('pending');
  const [commentDraft, setCommentDraft] = useState('');
  const [refundDraft, setRefundDraft] = useState('');
  const [tableKey, setTableKey] = useState(0);
  const [stats, setStats] = useState({ total: 0, pending: 0, processing: 0, delivery: 0, completed: 0, cancelled: 0 });
  const [resolvedStoreId, setResolvedStoreId] = useState(null);
  const [currentOrders, setCurrentOrders] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  const { GetPaginatedData, Patch, Get } = useApi();
  const { handleToast } = useToastHandler();
  const { currentStoreId } = useStore();

  useEffect(() => {
    let mounted = true;

    const resolveStore = async () => {
      if (currentStoreId) {
        setResolvedStoreId(currentStoreId);
        return;
      }

      try {
        const response = await Get('getAllStoresOfUser');
        const storeId =
          response?.first_store?.id ||
          response?.first_store?.store_id ||
          response?.stores?.[0]?.id ||
          response?.stores?.[0]?.store_id ||
          response?.results?.[0]?.id ||
          response?.results?.[0]?.store_id ||
          response?.[0]?.id ||
          response?.[0]?.store_id;
        if (mounted && storeId) setResolvedStoreId(storeId);
      } catch (error) {
        console.error('Unable to resolve store for ecommerce orders:', error);
      }
    };

    resolveStore();

    return () => {
      mounted = false;
    };
  }, [Get, currentStoreId]);

  useEffect(() => {
    setTableKey(prev => prev + 1);
  }, [resolvedStoreId]);

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds(prev => prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]);
  };

  const openOrder = useCallback((order) => {
    const nextStatus = normalizeStatus(order.status, order);
    setSelectedOrder(order);
    setStatusDraft(nextStatus);
    setCommentDraft(order.admin_comment || '');
    setRefundDraft(order.refund_note || '');
    setDetailsModal(true);
  }, []);

  const printOrders = (orders, includePrice = true, layout = 'thermal') => {
    if (!orders.length) {
      handleToast({ type: 'ERROR', message: 'Select at least one order to print' });
      return;
    }

    const isThermal = layout === 'thermal';
    const html = orders.map(order => `
      <section class="receipt">
        <div class="receipt-head">
          <h2>Order #${order.order_id || String(order.id).slice(0, 8)}</h2>
          <p>${new Date(order.created_at || Date.now()).toLocaleString('en-IN')}</p>
        </div>
        <div class="meta">
          <p><strong>Customer:</strong> ${order.full_name || '-'}</p>
          <p><strong>Phone:</strong> ${order.phone || '-'}</p>
          <p><strong>Address:</strong> ${getReadableAddress(order)}</p>
          <p><strong>Status:</strong> ${statusLabel(normalizeStatus(order.status, order))}</p>
        </div>
        <table>
          <thead><tr><th>Item</th><th>Qty</th>${includePrice ? '<th>Price</th><th>Total</th>' : ''}</tr></thead>
          <tbody>
            ${(order.items || []).map(item => `
              <tr>
                <td>${item.product_title || '-'}</td>
                <td>${item.quantity || 0}</td>
                ${includePrice ? `<td>${money(item.selling_price)}</td><td>${money(Number(item.selling_price || 0) * Number(item.quantity || 0))}</td>` : ''}
              </tr>
            `).join('')}
          </tbody>
        </table>
        ${includePrice ? `<h3>Total: ${money(getOrderTotal(order))}</h3>` : ''}
      </section>
    `).join('');

    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Order Receipts</title>
          <style>
            @page { size: ${isThermal ? '80mm auto' : 'A4'}; margin: ${isThermal ? '3mm' : '12mm'}; }
            body {
              font-family: ${isThermal ? "'Courier New', monospace" : "Arial, sans-serif"};
              padding: ${isThermal ? '0' : '10px'};
              color: #111827;
              font-size: ${isThermal ? '11px' : '13px'};
            }
            .receipt {
              width: ${isThermal ? '72mm' : '100%'};
              page-break-after: always;
              border: ${isThermal ? '0' : '1px solid #d1d5db'};
              padding: ${isThermal ? '0' : '18px'};
              margin: 0 auto ${isThermal ? '8px' : '20px'};
            }
            .receipt-head { text-align: center; border-bottom: 1px dashed #111; padding-bottom: 6px; margin-bottom: 8px; }
            h2 { margin: 0 0 4px; font-size: ${isThermal ? '14px' : '20px'}; }
            h3 { border-top: 1px dashed #111; padding-top: 8px; text-align: right; }
            p { margin: 3px 0; }
            .meta { border-bottom: 1px dashed #111; padding-bottom: 8px; margin-bottom: 8px; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            th, td {
              border-bottom: ${isThermal ? '1px dashed #aaa' : '1px solid #e5e7eb'};
              padding: ${isThermal ? '4px 2px' : '8px'};
              text-align: left;
              vertical-align: top;
            }
            th { background: ${isThermal ? 'transparent' : '#f3f4f6'}; }
            ${isThermal ? 'th:nth-child(n+3), td:nth-child(n+3) { text-align: right; }' : ''}
          </style>
        </head>
        <body>${html}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  const orderColumns = useMemo(() => [
    {
      header: '',
      field: 'select',
      render: (value, row) => (
        <input
          type="checkbox"
          checked={selectedOrderIds.includes(row.id)}
          onChange={() => toggleOrderSelection(row.id)}
        />
      )
    },
    { header: 'ORDER ID', field: 'order_id', render: (value, row) => `#${row.order_id || row.id?.slice(0, 8) || 'N/A'}` },
    {
      header: 'CUSTOMER',
      field: 'full_name',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: 700 }}>{row.full_name || '-'}</div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>{row.email || '-'}</div>
        </div>
      )
    },
    { header: 'PHONE', field: 'phone' },
    {
      header: 'STATUS',
      field: 'status',
      render: (value, row) => {
        const status = normalizeStatus(value, row);
        return <span className={`status-badge-ordrs status-${status}`}>{statusLabel(status)}</span>;
      }
    },
    { header: 'TOTAL', field: 'total_amount', render: (value, row) => money(getOrderTotal(row)) },
    { header: 'ITEMS', field: 'total_items', render: (value, row) => row.items?.length || 0 },
    { header: 'DATE', field: 'created_at', render: (value) => value ? new Date(value).toLocaleDateString('en-IN') : '-' },
    {
      header: 'MANAGE',
      field: 'manage',
      render: (value, row) => (
        <div className="order-row-actions">
          <button type="button" onClick={() => openOrder(row)} title="View and update order"><FaEye /> Manage</button>
          <button type="button" onClick={() => printOrders([row], true, 'thermal')} title="Thermal print with price"><FaPrint /> Thermal</button>
        </div>
      )
    }
  ], [openOrder, selectedOrderIds]);

  const fetchOrders = useCallback(async ({ page, search }) => {
    try {
      const queryParams = {};
      if (resolvedStoreId) queryParams.store_id = resolvedStoreId;
      if (filters.status) queryParams.status = filters.status;
      if (filters.from_date) queryParams.from_date = filters.from_date;
      if (filters.to_date) queryParams.to_date = filters.to_date;

      const res = await GetPaginatedData('checkout', {
        page,
        search: search && search.trim() ? search.trim() : '',
        queryParams
      });

      let orders = Array.isArray(res?.data) ? res.data : [];
      let totalCount = res?.totalCount || orders.length;

      if (Array.isArray(res?.rawResponse?.results)) {
        orders = res.rawResponse.results;
        totalCount = res.rawResponse.count || orders.length;
      }

      orders = orders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setCurrentOrders(orders);
      setSelectedOrderIds(prev => prev.filter(id => orders.some(order => order.id === id)));

      setStats({
        total: totalCount,
        pending: orders.filter(o => normalizeStatus(o.status, o) === 'pending').length,
        processing: orders.filter(o => normalizeStatus(o.status, o) === 'processing').length,
        delivery: orders.filter(o => ['ready_for_delivery', 'out_for_delivery'].includes(normalizeStatus(o.status, o))).length,
        completed: orders.filter(o => ['completed', 'delivered'].includes(normalizeStatus(o.status, o))).length,
        cancelled: orders.filter(o => ['cancelled', 'cancelled_stock_returned', 'refund_requested', 'refunded'].includes(normalizeStatus(o.status, o))).length
      });

      return { data: orders, totalPages: Math.max(1, Math.ceil(totalCount / 10)) };
    } catch (error) {
      console.error('Checkout API Error:', error);
      setCurrentOrders([]);
      return { data: [], totalPages: 1 };
    }
  }, [GetPaginatedData, filters, resolvedStoreId]);

  const statsCards = useMemo(() => [
    { label: 'Total Orders', value: stats.total, icon: FaShoppingCart, className: 'totalorders' },
    { label: 'Pending', value: stats.pending, icon: FaClock, className: 'pendingorders' },
    { label: 'Processing', value: stats.processing, icon: FaSpinner, className: 'processingorders' },
    { label: 'Delivery', value: stats.delivery, icon: FaTruck, className: 'deliveryorders' },
    { label: 'Done / Refund', value: stats.completed + stats.cancelled, icon: FaCheckCircle, className: 'completedorders' }
  ], [stats]);

  const handleStatusUpdate = async () => {
    if (!selectedOrder) return;
    try {
      await Patch('checkout', selectedOrder.id, {
        status: statusDraft,
        admin_comment: commentDraft,
        refund_note: refundDraft,
      });
      handleToast({ type: 'SUCCESS', message: 'Order updated' });
      setDetailsModal(false);
      setTableKey(prev => prev + 1);
    } catch (error) {
      console.error('Error updating order status:', error);
      handleToast({ type: 'ERROR', message: 'Failed to update order' });
    }
  };

  const handleFilterChange = (key, value) => {
    if (key === 'preset') {
      const range = getPresetRange(value);
      setFilters(prev => ({ ...prev, preset: value, ...range }));
      return;
    }
    setFilters(prev => ({ ...prev, [key]: value, preset: key.includes('date') ? 'custom' : prev.preset }));
  };

  const clearFilters = () => {
    setFilters({ status: '', preset: '', from_date: '', to_date: '' });
    setSelectedOrderIds([]);
    setTableKey(prev => prev + 1);
  };

  const selectedOrders = currentOrders.filter(order => selectedOrderIds.includes(order.id));

  return (
    <div className="order-management-container">
      <div className="order-stats-grid">
        {statsCards.map((stat, idx) => (
          <div key={idx} className="summary-card-ordrs">
            <div className="summary-card-inner">
              <div className={`summary-icon-ordrs ${stat.className}`}>
                <stat.icon size={24} />
              </div>
              <div className="summary-content">
                <span className="summary-label-orders">{stat.label}</span>
                <span className="summary-value-ordrs">{stat.value}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="order-filters-card">
        <div className="filters-header">
          <FaFilter /> <span>Order Filters</span>
        </div>
        <div className="filters-grid order-filter-grid">
          <div className="filter-group">
            <label>Status</label>
            <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
              <option value="">All Status</option>
              {STATUS_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Date Range</label>
            <select value={filters.preset} onChange={(e) => handleFilterChange('preset', e.target.value)}>
              {DATE_PRESETS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>From</label>
            <input type="date" value={filters.from_date} onChange={(e) => handleFilterChange('from_date', e.target.value)} />
          </div>
          <div className="filter-group">
            <label>To</label>
            <input type="date" value={filters.to_date} onChange={(e) => handleFilterChange('to_date', e.target.value)} />
          </div>
          <div className="filter-actions order-filter-actions">
            <CustomButton text="Apply" backgroundColor="rgb(74, 85, 70)" color="#fff" width="100%" height="38px" onClick={() => setTableKey(prev => prev + 1)} />
            <CustomButton text="Clear" backgroundColor="#ef4444" color="#fff" width="100%" height="38px" onClick={clearFilters} />
          </div>
        </div>
      </div>

      <div className="order-table-card">
        <div className="table-header order-table-toolbar">
          <div>
            <h3>All Orders</h3>
            <p>Select orders to print packing receipts or open Manage to contact/update/refund.</p>
          </div>
          <div className="print-actions">
            <button type="button" onClick={() => printOrders(selectedOrders, true, 'thermal')}><FaPrint /> Thermal With Price</button>
            <button type="button" onClick={() => printOrders(selectedOrders, false, 'thermal')}><FaPrint /> Thermal No Price</button>
            <button type="button" onClick={() => printOrders(selectedOrders, true, 'a4')}><FaPrint /> A4 With Price</button>
            <button type="button" onClick={() => printOrders(selectedOrders, false, 'a4')}><FaPrint /> A4 No Price</button>
          </div>
        </div>
        <ReusableTable
          key={tableKey}
          columns={orderColumns}
          fetchData={fetchOrders}
          showAction={false}
          showExport={true}
          enablePagination={true}
        />
      </div>

      <ReusableModal show={detailsModal} handleClose={() => setDetailsModal(false)} title="Manage Order" width="760px" height="auto">
        {selectedOrder && (
          <div className="order-details">
            <div className="order-detail-grid">
              <div className="detail-section">
                <h4>Customer Information</h4>
                <div className="detail-row"><span>Name:</span> <strong>{selectedOrder.full_name}</strong></div>
                <div className="detail-row"><span>Email:</span> <strong><a href={`mailto:${selectedOrder.email}`}>{selectedOrder.email}</a></strong></div>
                <div className="detail-row"><span>Phone:</span> <strong><a href={`tel:${selectedOrder.phone}`}>{selectedOrder.phone}</a></strong></div>
                <div className="detail-row"><span>Address:</span> <strong>{getReadableAddress(selectedOrder)}</strong></div>
              </div>

              <div className="detail-section">
                <h4>Order Information</h4>
                <div className="detail-row"><span>Order ID:</span> <strong>#{selectedOrder.order_id || selectedOrder.id?.slice(0, 8)}</strong></div>
                <div className="detail-row"><span>Total Amount:</span> <strong>{money(getOrderTotal(selectedOrder))}</strong></div>
                <div className="detail-row"><span>Total Items:</span> <strong>{selectedOrder.items?.length || 0}</strong></div>
                <div className="detail-row"><span>Payment:</span> <strong>{selectedOrder.payment_method}</strong></div>
                <div className="detail-row"><span>Date:</span> <strong>{selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString('en-IN') : '-'}</strong></div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Order Items</h4>
              {selectedOrder.items?.map((item, idx) => (
                <div key={idx} className="detail-row">
                  <span>{item.product_title} (x{item.quantity})</span>
                  <strong>{money(Number(item.selling_price || 0) * Number(item.quantity || 0))}</strong>
                </div>
              ))}
            </div>

            <div className="detail-section">
              <h4>Update Order</h4>
              <div className="order-update-grid">
                <label>
                  Status
                  <select value={statusDraft} onChange={(e) => setStatusDraft(e.target.value)}>
                    {STATUS_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <label>
                  Staff Comment
                  <textarea value={commentDraft} onChange={(e) => setCommentDraft(e.target.value)} placeholder="Delivery note, customer call note, packing instruction..." />
                </label>
                <label>
                  Refund Note
                  <textarea value={refundDraft} onChange={(e) => setRefundDraft(e.target.value)} placeholder="Reason, amount, approval note..." />
                </label>
              </div>
              <div className="status-actions">
                <CustomButton text="Save Order Update" backgroundColor="#0f766e" color="#fff" width="100%" height="42px" onClick={handleStatusUpdate} />
                <CustomButton text="Thermal Receipt" backgroundColor="#1f2937" color="#fff" width="100%" height="42px" onClick={() => printOrders([selectedOrder], true, 'thermal')} />
                <CustomButton text="Thermal Packing Slip" backgroundColor="#334155" color="#fff" width="100%" height="42px" onClick={() => printOrders([selectedOrder], false, 'thermal')} />
                <CustomButton text="A4 Receipt" backgroundColor="#475569" color="#fff" width="100%" height="42px" onClick={() => printOrders([selectedOrder], true, 'a4')} />
              </div>
            </div>
          </div>
        )}
      </ReusableModal>
    </div>
  );
};

export default OrderManagement;
